// src/api/teacherTasksAPI.js
import { ENV } from "../Utils";

/**
 * Controlador de front para Teacher Tasks (tareas secuenciales):
 * - Endpoints de teacher (crear/listar/activar/desactivar/eliminar/purgar/estadísticas)
 * - Endpoints de alumno (listar tareas, progreso, siguiente paso, responder, quizz final)
 */
export class TeacherTasksAPI {
  baseApi = ENV.BASE_API;
  route = ENV.API_ROUTES?.TEACHER_TASKS || "teachertasks";

  // -----------------------------
  // Helpers
  // -----------------------------
  buildTaskPayload({
    title,
    classnumber,
    steps = [],
    finalQuiz = null, // puede venir como string o { teacherQuizId }
    finalQuizRequired = false,
    schedule = { startAt: null, endAt: null },
  } = {}) {
    const normalizedSteps = (steps || [])
      .map((s, idx) => ({
        scene: String(s.scene || s._id || s.id).trim(),
        instruction: String(s.instruction || "").trim(),
        order: Number(s.order ?? idx + 1),
      }))
      .filter((s) => !!s.scene);

    // Normaliza a ObjectId string (no objeto)
    const normalizedFinalQuiz =
      finalQuiz && typeof finalQuiz === "object" && finalQuiz.teacherQuizId
        ? String(finalQuiz.teacherQuizId)
        : finalQuiz
          ? String(finalQuiz)
          : null;

    return {
      title: String(title || "").trim(),
      classnumber: String(classnumber || "").trim(),
      steps: normalizedSteps,
      finalQuiz: normalizedFinalQuiz, // <-- ObjectId directo
      finalQuizRequired: !!finalQuizRequired,
      schedule: schedule || { startAt: null, endAt: null },
    };
  }

  // -----------------------------
  // TEACHER
  // -----------------------------
  async createTask(accessToken, teacherId, classroomParam, data) {
    const classroomSlug = encodeURIComponent(String(classroomParam));
    const url = `${this.baseApi}/${this.route}/create/${teacherId}/${classroomSlug}`;
    const params = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    };
    const res = await fetch(url, params);
    const json = await res.json();
    if (res.status !== 201) throw json;
    return json; // { code:201, task:{...} }
  }

  async listTeacherTasks(accessToken, teacherId) {
    const url = `${this.baseApi}/${this.route}/list/${teacherId}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json; // { code:200, tasks:[...] }
  }

  async getTaskById(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/get/${taskId}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json; // { code:200, task:{...} }
  }

  async updateTask(accessToken, taskId, data) {
    const url = `${this.baseApi}/${this.route}/update/${taskId}`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async deleteDraftTask(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/delete/${taskId}`;
    const res = await fetch(url, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async activateTask(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/activate/${taskId}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async deactivateTask(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/deactivate/${taskId}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async purgeArchivedTask(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/purge/${taskId}`;
    const res = await fetch(url, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async reactivateTask(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/reactivate/${taskId}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async recomputeStats(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/stats/recompute/${taskId}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  // -----------------------------
  // STUDENT
  // -----------------------------
  async getStudentTasks(accessToken, studentId) {
    const url = `${this.baseApi}/${this.route}/students/${studentId}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async getStudentTaskProgress(accessToken, studentId, taskId) {
    const url = `${this.baseApi}/${this.route}/students/${studentId}/${taskId}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async getNextStep(accessToken, studentId, taskId) {
    const url = `${this.baseApi}/${this.route}/students/${studentId}/${taskId}/next`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async submitStepAnswer(
    accessToken,
    studentId,
    taskId,
    stepId,
    { mode, text } = {},
  ) {
    const url = `${this.baseApi}/${this.route}/students/${studentId}/${taskId}/steps/${stepId}/answer`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mode, text }),
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async markFinalQuizDone(accessToken, studentId, taskId, done = true) {
    const url = `${this.baseApi}/${this.route}/students/${studentId}/${taskId}/final-quiz/done`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ done: !!done }),
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  // -----------------------------
  // TEACHER: resultados / reset
  // -----------------------------
  async getTaskResults(accessToken, taskId) {
    const url = `${this.baseApi}/${this.route}/results/${taskId}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }

  async resetStudentProgress(accessToken, taskId, studentId) {
    const url = `${this.baseApi}/${this.route}/reset/${taskId}/${studentId}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const json = await res.json();
    if (res.status !== 200) throw json;
    return json;
  }
}
